import React from "react"

import {
  faMicrophone,
  faMicrophoneSlash,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { BackwardIcon, LanguageIcon } from "@heroicons/react/24/outline"
import { Subscription } from "@rails/actioncable"
import Tippy from "@tippyjs/react"

import { findGeminiLanguage } from "src/geminiLanguageOptions"

import StudentActions from "components/AudioRoom/RosterSidebar/StudentActions"

import { RosterEntry } from "../useRoster"

import "tippy.js/dist/tippy.css"

export type ExpectedStudent = {
  id: string
  fullName: string
  defaultLiveTranslationLanguageCode?: string | null
  defaultAudioPlaybackRate: number
}

type RosterListProps = {
  heading: string
  members: Array<RosterEntry | ExpectedStudent>
  channel?: Subscription
}

const RosterList: React.FC<RosterListProps> = ({
  heading,
  members,
  channel,
}) => (
  <section>
    <h2 className="mb-2 text-sm uppercase">
      {heading} ({members.length})
    </h2>
    {members.length === 0 ? (
      <p className="italic text-white/40">No students to show</p>
    ) : (
      <ul className="space-y-2">
        {members.map(member => {
          const isPresent = "attendeeId" in member
          const isStudent = isPresent && member.userType === "Student"
          const isUnmuted = isPresent && member.muted === false
          const languageLabel =
            findGeminiLanguage(member.defaultLiveTranslationLanguageCode)
              ?.label || member.defaultLiveTranslationLanguageCode

          return (
            <li
              key={isPresent ? member.attendeeId : member.id}
              className={`flex items-center justify-between gap-2 ${
                isPresent ? "" : "text-white/40"
              }`}
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span className="min-w-0 flex-1 break-words">
                  {member.fullName || "Participant"}
                </span>
                {member.defaultLiveTranslationLanguageCode && (
                  <Tippy content={`Listening in ${languageLabel}`}>
                    <span
                      className="flex-none"
                      aria-label="Translation enabled"
                    >
                      <LanguageIcon
                        className="h-5 w-5"
                        aria-label="Translation enabled"
                      />
                    </span>
                  </Tippy>
                )}
                {(member.defaultAudioPlaybackRate ?? 1) < 1 && (
                  <Tippy
                    content={`Listening at ${member.defaultAudioPlaybackRate}x speed`}
                  >
                    <span className="flex-none" aria-label="Audio slowed">
                      <BackwardIcon className="h-5 w-5" />
                    </span>
                  </Tippy>
                )}
              </div>
              {isStudent && channel ? (
                <StudentActions member={member} channel={channel} />
              ) : (
                isPresent && (
                  <Tippy content={isUnmuted ? "Unmuted" : "Muted"}>
                    <span
                      className="flex-none"
                      aria-label={isUnmuted ? "Unmuted" : "Muted"}
                    >
                      <FontAwesomeIcon
                        icon={isUnmuted ? faMicrophone : faMicrophoneSlash}
                        className={
                          isUnmuted ? "text-green-500" : "text-gray-400"
                        }
                      />
                    </span>
                  </Tippy>
                )
              )}
            </li>
          )
        })}
      </ul>
    )}
  </section>
)

export default RosterList
